import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { MatExpansionPanel } from '@angular/material/expansion';
import { combineLatest } from 'rxjs';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { RequirementData } from '../../../../shared/components/requirement-documents-section/requirement-documents-panel.model';
import { documentsSectionBlockedReason } from '../../form-details.data';
import { AutosaveResponseData, DocumentsSectionData, UpdateDocumentsData } from '../../form-details.model';
import { FormDetailsActionsService } from '../../services/actions/form-details-actions.service';
import { AutosaveFormDetailsFacade } from '../../services/autosave/autosave-form-details.facade';
import { FormDetailsSectionBlockedData, FormDetailsSectionHeaderIconType } from '../form-details-section-header/form-details-section-header.model';

@UntilDestroy()
@Component({
    selector: 'ifbp-documents-section',
    templateUrl: './documents-section.component.html',
    styleUrls: ['./documents-section.component.scss'],
    standalone: false
})
export class DocumentsSectionComponent implements OnInit {
  @ViewChild('panel', { static: true }) panel: MatExpansionPanel;

  @Input()
  documentsData: DocumentsSectionData;

  canMarkInvalidSection: boolean;
  sectionBlockedData: FormDetailsSectionBlockedData;
  wasPanelExpanded: boolean;

  private readonly notFoundRequirementIndex = -1;

  constructor(
    private readonly formDetailsActions: FormDetailsActionsService,
    private readonly autosaveFormDetailsFacade: AutosaveFormDetailsFacade

  ) { }

  ngOnInit(): void {
    this.initComponentStateActions()
  }

  private initComponentStateActions(): void {
    combineLatest([
      this.formDetailsActions.autosaveResponse$,
      this.autosaveFormDetailsFacade.isAutosaveInProgress$,
      this.panel.expandedChange
    ]).pipe(untilDestroyed(this))
      .subscribe(([autosaveData, isInProgress, isExpanded]: [AutosaveResponseData, boolean, boolean]) => {
        this.sectionBlockedData = this.getSectionBlockedData(autosaveData, isInProgress, isExpanded);
      });

    this.formDetailsActions.updateDocumentsData$
      .pipe(untilDestroyed(this))
      .subscribe((data: UpdateDocumentsData) => this.updateDocumentsData(data));

    this.formDetailsActions.expandAllSections$
      .pipe(untilDestroyed(this))
      .subscribe(() => this.expandPanel());
  }

  private getSectionBlockedData(data: AutosaveResponseData, isInProgress: boolean, isExpanded: boolean): FormDetailsSectionBlockedData {
    if (!this.wasPanelExpanded && isExpanded) {
      this.wasPanelExpanded = true;
    }

    if (!data?.params?.applicationId) {
      this.updateDocumentsData({ isUploadDisabled: true });
      return {
        isBlocked: this.wasPanelExpanded,
        reason: isInProgress ? "" : documentsSectionBlockedReason,
        isUnblockingInProgress: isInProgress
      };
    }

    if (this.sectionBlockedData?.isBlocked) {
      this.updateDocumentsData({ isUploadDisabled: false });
      return {
        isBlocked: false,
        isUnblockingInProgress: false
      }
    }

    return null;
  }

  private updateDocumentsData(data: UpdateDocumentsData): void {
    this.updateRequirement(data);
    this.updateValidationData(data);
    this.updateHeaderData();
  }

  private updateRequirement(data: UpdateDocumentsData): void {
    const index = this.documentsData?.requirements?.findIndex(
      req => req?.requirementId === data?.requirementChanges?.requirementId
    );

    if (index === this.notFoundRequirementIndex) {
      return;
    }

    const updatedRequirement: RequirementData = {
      ...this.documentsData.requirements[index],
      ...data.requirementChanges
    };

    this.documentsData.requirements[index] = updatedRequirement;
  }


  private updateValidationData(data: UpdateDocumentsData): void {
    this.canMarkInvalidSection = this.canMarkInvalidSection || data?.canMarkInvalidSection || false;
    this.documentsData.hasAnyDocumentChanges = this.documentsData.hasAnyDocumentChanges || data?.hasAnyDocumentChanges || false;
    this.documentsData.isValid = this.documentsData.requirements.every(req => req.isValid);
    this.documentsData.requirements.forEach(item => item.isUploadActionDisabled = data?.isUploadDisabled ?? item.isUploadActionDisabled)

    if (data?.canMarkInvalidSection && !this.documentsData.isValid) {
      this.expandPanel();
    }
  }

  private updateHeaderData(): void {
    this.documentsData.headerIconType = this.getHeaderIconType();
  }

  private getHeaderIconType(): FormDetailsSectionHeaderIconType {
    if (this.canMarkInvalidSection && !this.documentsData.isValid) {
      return FormDetailsSectionHeaderIconType.Warning
    }

    if (this.documentsData.hasAnyDocumentChanges) {
      return FormDetailsSectionHeaderIconType.Changes;
    }

    return FormDetailsSectionHeaderIconType.Default;
  }

  private expandPanel(): void {
    this.panel.open();
  }
}
